import api from './api';
import { User, UserRole, Vehicle } from '../types';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

interface CreateUserData {
  email: string;
  password: string;
  fullName: string;
  cpfCnpj?: string;
  telefone?: string;
  nivelAcesso: UserRole;
  proprietarioId?: string;
  statusAtivo: boolean;
}

interface UpdateUserData {
  email?: string;
  password?: string;
  fullName?: string;
  cpfCnpj?: string;
  telefone?: string;
  nivelAcesso?: UserRole;
  proprietarioId?: string;
  statusAtivo?: boolean;
}

export const userService = {
  // Listar todos os usuários (admin only)
  getAllUsers: async (): Promise<ApiResponse<User[]>> => {
    try {
      const response = await api.get('/users');
      return response.data; // Backend já retorna ApiResponse
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao buscar usuários',
      };
    }
  },

  // Buscar usuário por ID
  getUserById: async (id: string): Promise<ApiResponse<User>> => {
    try {
      const response = await api.get(`/users/${id}`);
      return response.data; // Backend já retorna ApiResponse
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao buscar usuário',
      };
    }
  },

  // Criar novo usuário
  createUser: async (data: CreateUserData): Promise<ApiResponse<User>> => {
    try {
      const response = await api.post('/users', data);
      return response.data; // Backend já retorna ApiResponse
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao criar usuário',
      };
    }
  },

  // Atualizar usuário
  updateUser: async (id: string, data: UpdateUserData): Promise<ApiResponse<User>> => {
    try {
      const response = await api.put(`/users/${id}`, data);
      return response.data; // Backend já retorna ApiResponse
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao atualizar usuário',
      };
    }
  },

  // Deletar usuário
  deleteUser: async (id: string): Promise<ApiResponse<void>> => {
    try {
      const response = await api.delete(`/users/${id}`);
      return response.data; // Backend já retorna ApiResponse
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao deletar usuário',
      };
    }
  },

  // Buscar proprietários (para dropdown)
  getProprietarios: async (): Promise<ApiResponse<User[]>> => {
    try {
      const response = await api.get('/users/role/PROPRIETARIO');
      return response.data; // Backend já retorna ApiResponse
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao buscar proprietários',
      };
    }
  },

  // Cartão do motorista (suporte)

  // Desvincula o aparelho (celular perdido ou trocado); o motorista entra em outro aparelho
  releaseDevice: async (id: string): Promise<ApiResponse<void>> => {
    try {
      const response = await api.delete(`/admin/users/${id}/device`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao desvincular aparelho',
      };
    }
  },

  // Foto do cartão: não tem URL pública, vem da API autenticada
  getPhotoUrl: async (id: string): Promise<string | null> => {
    try {
      const response = await api.get(`/admin/users/${id}/photo`, { responseType: 'blob' });
      return URL.createObjectURL(response.data);
    } catch {
      return null;
    }
  },

  // Remove a foto para o motorista tirar outra
  deletePhoto: async (id: string): Promise<ApiResponse<void>> => {
    try {
      const response = await api.delete(`/admin/users/${id}/photo`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao remover foto',
      };
    }
  },

  getVehicles: async (id: string): Promise<ApiResponse<Vehicle[]>> => {
    try {
      const response = await api.get(`/vehicles/user/${id}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao buscar veículos',
      };
    }
  },

  // Remove o veículo para o motorista cadastrar outra placa
  deleteVehicle: async (vehicleId: string): Promise<ApiResponse<void>> => {
    try {
      const response = await api.delete(`/vehicles/${vehicleId}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao remover veículo',
      };
    }
  },
};
